import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Trash2, UserPlus } from 'lucide-react';
import api from '../../services/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { COMPANY_ROLES, companyKind, displayName, roleLabel } from './adminRoles';
import RolePermissionsInfo from './RolePermissionsInfo';

const BLUE = '#0041C2';
const MANAGER_ROLE = 'COMPANY_MANAGER';
const RECRUITER_ROLE = 'COMPANY_USER';
const VIEWER_ROLE = 'USER';
// Roles an admin can assign; a user may hold several (capabilities add up).
const ASSIGNABLE_ROLES = [
  { value: 'COMPANY_ADMIN', label: 'Admin' },
  { value: MANAGER_ROLE, label: 'Manager' },
  { value: RECRUITER_ROLE, label: 'Recruiter' },
  { value: VIEWER_ROLE, label: 'Viewer' },
];

function memberRoles(member) {
  const list = Array.isArray(member?.roles) && member.roles.length ? member.roles : [member?.role];
  return list.filter(Boolean);
}

function isSelfMember(member, user) {
  return String(member?.userId ?? member?.user_id) === String(user?.id)
    || (!!member?.email && String(member.email).toLowerCase() === String(user?.email || '').toLowerCase());
}

function isAdminMember(member) {
  return memberRoles(member).some((r) => r === 'TENANT_ADMIN' || r === 'COMPANY_ADMIN' || r === 'COMPANY_OWNER');
}

function RolePicker({ value, onChange, disabled, locked = [] }) {
  const toggle = (role) => {
    if (locked.includes(role)) return;
    const next = value.includes(role) ? value.filter((r) => r !== role) : [...value, role];
    onChange(next);
  };
  return (
    <span style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {ASSIGNABLE_ROLES.map((opt) => (
        <label key={opt.value} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12, cursor: disabled ? 'default' : 'pointer' }}>
          <input
            type="checkbox"
            checked={value.includes(opt.value)}
            disabled={disabled || locked.includes(opt.value)}
            onChange={() => toggle(opt.value)}
          />
          {opt.label}
        </label>
      ))}
    </span>
  );
}

function sessionKind(tenantRole, role, user) {
  const held = Array.isArray(user?.tenantRoles) ? user.tenantRoles : [];
  const kinds = [...held, tenantRole, role, user?.tenantRole, user?.role].map(companyKind);
  if (kinds.includes(COMPANY_ROLES.owner) || kinds.includes(COMPANY_ROLES.admin)) return COMPANY_ROLES.admin;
  if (kinds.includes(COMPANY_ROLES.manager)) return COMPANY_ROLES.manager;
  if (kinds.includes(COMPANY_ROLES.recruiter)) return COMPANY_ROLES.recruiter;
  return COMPANY_ROLES.admin;
}

function badgeColors(kind) {
  if (kind === COMPANY_ROLES.manager) return { background: '#F5C518', color: '#3D2E00' };
  if (kind === COMPANY_ROLES.recruiter) return { background: '#22A06B', color: '#fff' };
  return { background: '#1B3A4B', color: '#fff' };
}

export default function TenantAdminView({ onOpenRules }) {
  const { user, role, tenantRole, refetchUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [addRoles, setAddRoles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editRoles, setEditRoles] = useState([]);

  const kind = sessionKind(tenantRole, role, user);
  const isManager = kind === COMPANY_ROLES.manager;
  const isRecruiter = kind === COMPANY_ROLES.recruiter;
  const isAdmin = !isManager && !isRecruiter;
  const addRole = isRecruiter ? VIEWER_ROLE : isManager ? RECRUITER_ROLE : MANAGER_ROLE;
  const memberRoleLabel = isRecruiter ? 'Viewer' : isManager ? 'Recruiter' : 'Manager';
  const heldRoles = Array.isArray(user?.tenantRoles) ? user.tenantRoles : [];
  const badgeLabel = heldRoles.length > 1
    ? heldRoles.map((r) => roleLabel(r)).join(' + ')
    : isRecruiter ? 'Recruiter' : isManager ? 'Manager' : 'Admin';
  const colors = badgeColors(kind);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/my-tenant');
      setData(res.data);
      setError(null);
    } catch {
      setData(null);
      setError('Could not load this company\'s users.');
      toast.error('Failed to load company users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tenant = data?.tenant;
  const tenantId = tenant?.id;
  const members = Array.isArray(data?.users) ? data.users : [];
  const userCount = members.length;
  const headerName = displayName(user) || user?.email || badgeLabel;
  const tenantLabel = tenant?.name || '—';

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!tenantId || !addEmail.trim()) return;
    setSaving(true);
    try {
      const roles = isAdmin ? addRoles : [addRole];
      if (!roles.length) {
        toast.error('Pick at least one role');
        setSaving(false);
        return;
      }
      await api.post(`/tenants/${tenantId}/users`, {
        email: addEmail.trim(),
        roles,
        role: roles[0],
        invitedByUserId: String(user?.id || ''),
      });
      toast.success('User added');
      setAddEmail('');
      setAddRoles([]);
      setAdding(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setEditRoles(memberRoles(member).map((r) => (r === 'TENANT_ADMIN' || r === 'COMPANY_OWNER' ? 'COMPANY_ADMIN' : r)));
  };

  const saveRoles = async (member) => {
    const memberUserId = member?.userId ?? member?.user_id;
    if (!editRoles.length) {
      toast.error('Pick at least one role');
      return;
    }
    try {
      await api.put(`/tenants/${tenantId}/users/${memberUserId}`, { roles: editRoles, role: editRoles[0] });
      toast.success('Roles updated');
      setEditingId(null);
      fetchData();
      // Own roles changed → refresh the session so the menu and permissions follow at once.
      if (String(memberUserId) === String(user?.id) && refetchUser) await refetchUser();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update roles');
    }
  };

  const handleRemoveUser = async (event, member) => {
    event?.stopPropagation?.();
    const memberUserId = member?.userId ?? member?.user_id;
    if (!tenantId || memberUserId == null) {
      toast.error('Cannot remove this user — missing user id.');
      return;
    }
    if (!window.confirm(`Remove ${member.email} from ${tenantLabel}?`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/users/${memberUserId}`);
      setData((prev) => prev
        ? { ...prev, users: (prev.users || []).filter((row) => String(row.userId ?? row.user_id) !== String(memberUserId)) }
        : prev);
      toast.success('User removed');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove user');
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (error || !tenant) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 0 40px' }}>
        <p style={{ color: '#6B7280' }}>{error || 'No company is assigned to this session.'}</p>
        <button
          type="button"
          onClick={fetchData}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: BLUE, color: '#fff', fontWeight: 600 }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 0 40px' }}>
      {onOpenRules && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', border: '1px solid #E5E7EB',
          borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 12, color: '#374151' }}>
            Who can see and do what (data visibility, creating positions, WhatsApp, deleting, ...) is set in <b>Rules &amp; Permissions</b>.
          </div>
          <button
            type="button"
            onClick={onOpenRules}
            style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${BLUE}`, background: 'transparent', color: BLUE, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            Open Rules &amp; Permissions
          </button>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          User Management <RolePermissionsInfo />
        </h1>
        <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>
          You, the other admins of this tenant, and the users you added.
        </p>
      </div>

      <div style={{
        border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', background: '#fff',
      }}>
        <div
          onClick={() => setExpanded((open) => !open)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
            cursor: 'pointer', userSelect: 'none',
          }}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{headerName}</span>
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1.4,
                background: colors.background,
                color: colors.color,
                padding: '2px 7px',
                borderRadius: 4,
              }}>
                {badgeLabel}
              </span>
            </div>
            <span style={{ fontSize: 11, color: '#6B7280' }}>{tenantLabel}</span>
          </div>
          <span style={{ fontSize: 12, color: '#6B7280' }}>{userCount} {userCount === 1 ? 'user' : 'users'}</span>
        </div>

        {expanded && (
          <div style={{ borderTop: '1px solid #E5E7EB', padding: '12px 16px' }}>
            {adding ? (
              <form onSubmit={handleAddUser} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <input
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="User email *"
                  required
                  type="email"
                  style={{ flex: 2, minWidth: 180, padding: '7px 10px', borderRadius: 7, border: '1px solid #D1D5DB', fontSize: 12 }}
                />
                {isAdmin ? (
                  <RolePicker value={addRoles} onChange={setAddRoles} disabled={saving} />
                ) : (
                  <span style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #D1D5DB', fontSize: 12, color: '#374151', background: '#F9FAFB' }}>
                    {memberRoleLabel}
                  </span>
                )}
                <button type="submit" disabled={saving} style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: BLUE, color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                  {saving ? 'Adding…' : 'Add'}
                </button>
                <button type="button" onClick={() => { setAdding(false); setAddEmail(''); setAddRoles([]); }} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #D1D5DB', background: 'transparent', fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => { setAddEmail(''); setAddRoles([]); setAdding(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: BLUE,
                  background: 'transparent', border: `1px solid ${BLUE}`, padding: '5px 12px',
                  borderRadius: 7, cursor: 'pointer', marginBottom: 10, fontWeight: 500,
                }}
              >
                <UserPlus size={13} /> Add User
              </button>
            )}

            {members.length === 0 ? (
              <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>No users added by you in this tenant yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Role</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '8px 8px' }}>{displayName(member)}</td>
                      <td style={{ padding: '8px 8px', color: '#6B7280' }}>{member.email}</td>
                      <td style={{ padding: '8px 8px' }}>
                        {editingId === member.id ? (
                          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <RolePicker
                              value={editRoles}
                              onChange={setEditRoles}
                              locked={isSelfMember(member, user) || isAdminMember(member) ? ['COMPANY_ADMIN'] : []}
                            />
                            <button type="button" onClick={() => saveRoles(member)} style={{ padding: '3px 10px', borderRadius: 6, border: 'none', background: BLUE, color: '#fff', fontSize: 11, cursor: 'pointer' }}>Save</button>
                            <button type="button" onClick={() => setEditingId(null)} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #D1D5DB', background: 'transparent', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                          </span>
                        ) : (
                          memberRoles(member).map((r) => roleLabel(r)).join(' + ')
                        )}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {isSelfMember(member, user) ? (
                          // Admins may add roles to themselves (Admin stays locked on); no self-delete.
                          isAdmin && editingId !== member.id ? (
                            <button
                              type="button"
                              onClick={() => startEdit(member)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: BLUE, padding: 4 }}
                              aria-label="Edit your roles"
                              title="Edit your roles (Admin stays on)"
                            >
                              <Pencil size={13} />
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: '#9CA3AF' }}>You</span>
                          )
                        ) : (
                          <>
                            {isAdmin && editingId !== member.id && (
                              <button
                                type="button"
                                onClick={() => startEdit(member)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: BLUE, padding: 4 }}
                                aria-label={`Edit roles for ${member.email}`}
                              >
                                <Pencil size={13} />
                              </button>
                            )}
                            {/* Admins are removed by the Service Provider (Admin Portal), not here. */}
                            {!isAdminMember(member) && <button
                              type="button"
                              onClick={(event) => handleRemoveUser(event, member)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}
                              aria-label={`Remove ${member.email}`}
                            >
                              <Trash2 size={13} />
                            </button>}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
